import React, { useState } from 'react';
import { SkillsTrackerProvider, useSkillsTracker } from '../contexts/SkillsTrackerContext';
import { Target, TrendingUp, Award, Plus, Filter, Search, Activity, BarChart3, Download } from 'lucide-react';
import { SkillEditModal } from '../components/SkillEditModal';
import { AddCustomSkillModal } from '../components/AddCustomSkillModal';
import { ProgressLoggingModal } from '../components/ProgressLoggingModal';
import { SkillsAnalyticsDashboard } from '../components/SkillsAnalyticsDashboard';
import { ImportSkillsModal } from '../components/ImportSkillsModal';

const SkillsTrackerDashboard: React.FC = () => {
  const { skills, summary, loading, error, refreshSkills } = useSkillsTracker();
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [editingSkill, setEditingSkill] = useState<any>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [loggingProgressSkill, setLoggingProgressSkill] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'skills' | 'analytics'>('skills');

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your skills...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 mb-4">Error loading skills: {error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const prioritySkills = skills
    .filter(s => s.status === 'active')
    .sort((a, b) => a.priority - b.priority)
    .slice(0, 5);

  const filteredSkills = skills.filter(skill =>
    searchTerm === '' ||
    skill.skill.skill_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    skill.skill.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header with Tabs */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Skills Tracker</h1>
              <p className="text-gray-600">Track and manage your skill development journey</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setActiveTab('skills')}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                  activeTab === 'skills'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Target className="w-5 h-5" />
                Skills
              </button>
              <button
                onClick={() => setActiveTab('analytics')}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                  activeTab === 'analytics'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <BarChart3 className="w-5 h-5" />
                Analytics
              </button>
            </div>
          </div>
        </div>

        {/* Conditional Content */}
        {activeTab === 'analytics' ? (
          <SkillsAnalyticsDashboard />
        ) : (
          <>
        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600 text-sm">Total Skills</span>
              <Target className="w-5 h-5 text-blue-600" />
            </div>
            <p className="text-3xl font-bold text-gray-900">{summary?.total_skills || 0}</p>
            <p className="text-sm text-gray-500 mt-1">
              {summary?.active_skills || 0} active
            </p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600 text-sm">Avg Progress</span>
              <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
            <p className="text-3xl font-bold text-gray-900">{summary?.average_progress || 0}%</p>
            <p className="text-sm text-gray-500 mt-1">
              {summary?.completed_skills || 0} completed
            </p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600 text-sm">High Priority</span>
              <Award className="w-5 h-5 text-yellow-600" />
            </div>
            <p className="text-3xl font-bold text-gray-900">
              {(summary?.skills_by_priority.priority_1 || 0) + (summary?.skills_by_priority.priority_2 || 0)}
            </p>
            <p className="text-sm text-gray-500 mt-1">Critical & High</p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600 text-sm">Timeline</span>
              <Target className="w-5 h-5 text-purple-600" />
            </div>
            <p className="text-3xl font-bold text-gray-900">
              {summary?.estimated_completion_months_total || 0}
            </p>
            <p className="text-sm text-gray-500 mt-1">months total</p>
          </div>
        </div>

        {/* Priority Skills Section */}
        {prioritySkills.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Priority Skills</h2>
              <span className="text-sm text-gray-500">Top 5 skills to focus on</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {prioritySkills.map(skill => (
                <PrioritySkillCard
                  key={skill.id}
                  skill={skill}
                  onEdit={() => setEditingSkill(skill)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search skills..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <Filter className="w-5 h-5" />
            Filters
          </button>
          <button
            onClick={() => setShowImportModal(true)}
            className="px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 flex items-center gap-2"
          >
            <Download className="w-5 h-5" />
            Import from Report
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Add Skill
          </button>
        </div>

        {/* Skills Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredSkills.map(skill => (
            <SkillCard
              key={skill.id}
              skill={skill}
              onEdit={() => setEditingSkill(skill)}
              onLogProgress={() => setLoggingProgressSkill(skill)}
            />
          ))}
        </div>

        {filteredSkills.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">
              {searchTerm ? 'No skills match your search' : 'No skills added yet'}
            </p>
            {!searchTerm && (
              <div className="space-y-3">
                <div className="flex items-center justify-center gap-3">
                  <button
                    onClick={() => setShowImportModal(true)}
                    className="px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 flex items-center gap-2"
                  >
                    <Download className="w-5 h-5" />
                    Import from Report
                  </button>
                  <span className="text-gray-400">or</span>
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                  >
                    <Plus className="w-5 h-5" />
                    Add Manually
                  </button>
                </div>
                <p className="text-sm text-gray-500">
                  Import skills from your career intelligence report or add them manually
                </p>
              </div>
            )}
          </div>
        )}

        </>
        )}

        {/* Modals */}
        <SkillEditModal
          skill={editingSkill}
          isOpen={editingSkill !== null}
          onClose={() => setEditingSkill(null)}
          onSave={refreshSkills}
        />

        <AddCustomSkillModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={refreshSkills}
        />

        <ProgressLoggingModal
          skill={loggingProgressSkill}
          isOpen={loggingProgressSkill !== null}
          onClose={() => setLoggingProgressSkill(null)}
          onSuccess={refreshSkills}
        />

        <ImportSkillsModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          onSuccess={refreshSkills}
        />
      </div>
    </div>
  );
};

const PrioritySkillCard: React.FC<{ skill: any; onEdit: () => void }> = ({ skill, onEdit }) => {
  const getPriorityColor = (priority: number) => {
    if (priority === 1) return 'text-red-600 bg-red-100';
    if (priority === 2) return 'text-orange-600 bg-orange-100';
    if (priority === 3) return 'text-yellow-600 bg-yellow-100';
    if (priority === 4) return 'text-blue-600 bg-blue-100';
    return 'text-gray-600 bg-gray-100';
  };

  const getPriorityLabel = (priority: number) => {
    if (priority === 1) return 'Critical';
    if (priority === 2) return 'High';
    if (priority === 3) return 'Medium';
    if (priority === 4) return 'Low';
    return 'Optional';
  };

  const getProgressColor = (progress: number) => {
    if (progress >= 75) return 'bg-green-500';
    if (progress >= 50) return 'bg-blue-500';
    if (progress >= 25) return 'bg-yellow-500';
    return 'bg-gray-300';
  };

  return (
    <div onClick={onEdit} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer">
      <div className="flex items-start justify-between mb-3">
        <h3 className="text-lg font-semibold text-gray-900 flex-1">{skill.skill.skill_name}</h3>
        <span className={`px-2 py-1 rounded text-xs font-medium ${getPriorityColor(skill.priority)}`}>
          {getPriorityLabel(skill.priority)}
        </span>
      </div>

      <p className="text-sm text-gray-600 mb-4">{skill.skill.category}</p>

      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm text-gray-600">Progress</span>
          <span className="text-sm font-semibold text-gray-900">{skill.progress_percentage}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all ${getProgressColor(skill.progress_percentage)}`}
            style={{ width: `${skill.progress_percentage}%` }}
          />
        </div>
      </div>

      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-600">
          {skill.current_proficiency_level} → {skill.target_proficiency_level}
        </span>
        {skill.estimated_months_to_complete && (
          <span className="text-gray-500">{skill.estimated_months_to_complete}mo</span>
        )}
      </div>
    </div>
  );
};

const SkillCard: React.FC<{ skill: any; onEdit: () => void; onLogProgress?: () => void }> = ({ skill, onEdit, onLogProgress }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'text-green-600 bg-green-100';
      case 'completed':
        return 'text-blue-600 bg-blue-100';
      case 'paused':
        return 'text-yellow-600 bg-yellow-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <h3 className="text-lg font-semibold text-gray-900">{skill.skill.skill_name}</h3>
        <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(skill.status)}`}>
          {skill.status}
        </span>
      </div>

      <p className="text-sm text-gray-600 mb-4">
        {skill.skill.category} {skill.skill.subcategory && `• ${skill.skill.subcategory}`}
      </p>

      <div className="space-y-3">
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-gray-600">Progress</span>
            <span className="text-sm font-semibold">{skill.progress_percentage}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="h-2 rounded-full bg-blue-500 transition-all"
              style={{ width: `${skill.progress_percentage}%` }}
            />
          </div>
        </div>

        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">Priority</span>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map(i => (
              <div
                key={i}
                className={`w-4 h-4 rounded ${
                  i <= skill.priority ? 'bg-yellow-400' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
        </div>

        {skill.target_completion_date && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Target Date</span>
            <span className="text-gray-900">
              {new Date(skill.target_completion_date).toLocaleDateString()}
            </span>
          </div>
        )}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2">
        <button
          onClick={(e) => { e.stopPropagation(); onEdit(); }}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Edit
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onLogProgress && onLogProgress(); }}
          className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-1"
        >
          <Activity className="w-4 h-4" />
          Log
        </button>
      </div>
    </div>
  );
};

const SkillsTrackerPage: React.FC = () => {
  return (
    <SkillsTrackerProvider>
      <SkillsTrackerDashboard />
    </SkillsTrackerProvider>
  );
};

export default SkillsTrackerPage;
